import { toDateInputValue } from '../../shared/utils/localization';
import { Contact } from '../contacts/types/Contact';
import { Invoice, InvoiceLineRequest, InvoiceLineType } from './types/Invoice';

export type DraftLine = {
  key: string;
  lineType: InvoiceLineType;
  productId: string;
  serviceOfferingId: string;
  name: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  taxPercent: string;
};

export const createEmptyLine = (): DraftLine => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  lineType: 'custom',
  productId: '',
  serviceOfferingId: '',
  name: '',
  quantity: '1',
  unitPrice: '',
  discountPercent: '',
  taxPercent: '',
});

export const parseNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const FALLBACK_CURRENCY = 'AZN';

export const formatMoney = (value?: number, currency?: string) => {
  if (value === undefined) return '-';
  return `${value.toFixed(2)} ${currency?.trim() || FALLBACK_CURRENCY}`;
};

export const pickDefaultLocationId = (locations: { id: string; isPrimary?: boolean }[]) =>
  locations.find(location => location.isPrimary)?.id || (locations.length === 1 ? locations[0].id : '');

export const toDateInput = (value?: string) => toDateInputValue(value);

export const toIsoDate = (value: string) => (value.trim() ? `${value.trim()}T00:00:00.000Z` : undefined);

const round = (amount: number) => Math.round(amount * 100) / 100;

export const previewLineTotal = (line: DraftLine, pricesIncludeTax: boolean): number => {
  const quantity = parseNumber(line.quantity) ?? 0;
  const unitPrice = parseNumber(line.unitPrice) ?? 0;
  const discountPercent = parseNumber(line.discountPercent) ?? 0;
  const taxPercent = parseNumber(line.taxPercent) ?? 0;
  const discounted = unitPrice * quantity * (1 - discountPercent / 100);
  if (pricesIncludeTax) {
    return round(discounted);
  }
  return round(discounted + discounted * taxPercent / 100);
};

export const previewInvoiceTotals = (lines: DraftLine[], pricesIncludeTax: boolean) => {
  let subtotal = 0;
  let taxTotal = 0;
  let total = 0;
  lines.forEach(line => {
    const quantity = parseNumber(line.quantity) ?? 0;
    const unitPrice = parseNumber(line.unitPrice) ?? 0;
    const discountPercent = parseNumber(line.discountPercent) ?? 0;
    const taxPercent = parseNumber(line.taxPercent) ?? 0;
    const discounted = round(unitPrice * quantity * (1 - discountPercent / 100));
    if (pricesIncludeTax) {
      const lineTax = taxPercent ? round(discounted - discounted / (1 + taxPercent / 100)) : 0;
      total += discounted;
      taxTotal += lineTax;
      subtotal += round(discounted - lineTax);
      return;
    }
    const lineTax = round(discounted * taxPercent / 100);
    subtotal += discounted;
    taxTotal += lineTax;
    total += round(discounted + lineTax);
  });
  return { subtotal: round(subtotal), taxTotal: round(taxTotal), total: round(total) };
};

export const linesToRequest = (lines: DraftLine[]): InvoiceLineRequest[] =>
  lines.map(line => ({
    lineType: line.lineType,
    productId: line.lineType === 'product' ? line.productId || undefined : undefined,
    serviceOfferingId: line.lineType === 'service' ? line.serviceOfferingId || undefined : undefined,
    name: line.name.trim() || undefined,
    quantity: parseNumber(line.quantity) ?? 0,
    unitPrice: parseNumber(line.unitPrice),
    discountPercent: parseNumber(line.discountPercent),
    taxPercent: parseNumber(line.taxPercent),
  }));

export const invoiceToDraftLines = (invoice: Invoice): DraftLine[] => {
  if (invoice.lines.length === 0) return [createEmptyLine()];
  return invoice.lines.map(line => ({
    key: line.id,
    lineType: line.lineType,
    productId: line.productId || '',
    serviceOfferingId: line.serviceOfferingId || '',
    name: line.name,
    quantity: String(line.quantity),
    unitPrice: String(line.unitPrice),
    discountPercent: line.discountPercent ? String(line.discountPercent) : '',
    taxPercent: line.taxPercent ? String(line.taxPercent) : '',
  }));
};

export type BillToDraft = {
  name: string;
  emails: string[];
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateOrProvince: string;
  postalCode: string;
  country: string;
};

export type PartyAddress = {
  name?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  taxIdLabel?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  country?: string;
};

export const emptyBillTo = (): BillToDraft => ({
  name: '',
  emails: [''],
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateOrProvince: '',
  postalCode: '',
  country: '',
});

export const parseBillToEmails = (value?: string): string[] => {
  const emails = (value || '')
    .split(/[;,\n]/)
    .map(email => email.trim())
    .filter(Boolean);
  return emails.length > 0 ? emails : [''];
};

export const joinBillToEmails = (emails: string[]): string =>
  emails.map(email => email.trim()).filter(Boolean).join(', ');

export const validBillToEmails = (emails: string[]): string[] =>
  emails
    .map(email => email.trim())
    .filter(email => email.includes('@') && email.includes('.'));

export const formatContactPhone = (contact: Contact) =>
  [contact.phoneCountryCode, contact.phone].filter(Boolean).join(' ').trim();

export const invoiceToBillTo = (invoice: Invoice): BillToDraft => ({
  name: invoice.billToName || invoice.contactName || '',
  emails: parseBillToEmails(invoice.billToEmail),
  phone: invoice.billToPhone || '',
  addressLine1: invoice.billToAddressLine1 || '',
  addressLine2: invoice.billToAddressLine2 || '',
  city: invoice.billToCity || '',
  stateOrProvince: invoice.billToStateOrProvince || '',
  postalCode: invoice.billToPostalCode || '',
  country: invoice.billToCountry || '',
});

export const applyContactToBillTo = (previous: BillToDraft, contact: Contact): BillToDraft => ({
  ...previous,
  name: contact.name || previous.name,
  emails: contact.email ? [contact.email] : previous.emails,
  phone: formatContactPhone(contact),
});

export const formatPartyLines = (party: PartyAddress): string[] => {
  const cityLine = [party.postalCode, party.city].filter(Boolean).join(' ');
  const taxLine = party.taxId
    ? (party.taxIdLabel ? `${party.taxIdLabel}: ${party.taxId}` : party.taxId)
    : '';
  return [
    party.name,
    taxLine,
    party.email,
    party.phone,
    party.addressLine1,
    party.addressLine2,
    cityLine,
    party.stateOrProvince,
    party.country,
  ].map(value => (value || '').trim()).filter(Boolean);
};

export const sellerFromInvoice = (invoice: Invoice, taxIdLabel: string): PartyAddress => ({
  name: invoice.sellerName,
  email: invoice.sellerEmail,
  phone: invoice.sellerPhone,
  taxId: invoice.sellerTaxId,
  taxIdLabel,
  addressLine1: invoice.sellerAddressLine1,
  addressLine2: invoice.sellerAddressLine2,
  city: invoice.sellerCity,
  stateOrProvince: invoice.sellerStateOrProvince,
  postalCode: invoice.sellerPostalCode,
  country: invoice.sellerCountry,
});

export const billToCreatePayload = (billTo: BillToDraft) => ({
  billToName: billTo.name.trim() || undefined,
  billToEmail: joinBillToEmails(billTo.emails) || undefined,
  billToPhone: billTo.phone.trim() || undefined,
  billToAddressLine1: billTo.addressLine1.trim() || undefined,
  billToAddressLine2: billTo.addressLine2.trim() || undefined,
  billToCity: billTo.city.trim() || undefined,
  billToStateOrProvince: billTo.stateOrProvince.trim() || undefined,
  billToPostalCode: billTo.postalCode.trim() || undefined,
  billToCountry: billTo.country.trim() || undefined,
});

export const billToUpdatePayload = (billTo: BillToDraft) => ({
  ...billToCreatePayload(billTo),
  clearBillToEmail: joinBillToEmails(billTo.emails).length === 0,
  clearBillToPhone: !billTo.phone.trim(),
  clearBillToAddressLine1: !billTo.addressLine1.trim(),
  clearBillToAddressLine2: !billTo.addressLine2.trim(),
  clearBillToCity: !billTo.city.trim(),
  clearBillToStateOrProvince: !billTo.stateOrProvince.trim(),
  clearBillToPostalCode: !billTo.postalCode.trim(),
  clearBillToCountry: !billTo.country.trim(),
});
